/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ChartView } from 'echarts';
import { graphic } from 'echarts/core';
import { type BoundingRect } from '../../../model/util/coord';

/** 旗子标记对应的信息 */
export interface FlagLayoutInfo {
  /** 旗子标记的方框对应的zrender元素 */
  rect: graphic.Rect;
  /** 旗子标记的文本对应的zrender元素 */
  label: graphic.Text;
  /** 旗子标记的旗杆对应的zrender元素 */
  line: graphic.Line;
  /** 旗子标记的整体容器对应的zrender元素 */
  group: graphic.Group;
  /** 旗子标记的圆点对应的zrender元素 */
  circle: graphic.Circle;
  /** 旗子标记的文本和方框所在容器对应的zrender元素 */
  insideGroup: graphic.Group;
  /** 旗子标记的文本和方框对应的包围矩形的信息 */
  boundRect: BoundingRect;
  /** 旗子标记的显示优先级 */
  weight: number;
}
export type FlagInfo = Pick<FlagLayoutInfo, 'rect' | 'insideGroup' | 'line' | 'boundRect' | 'group'> & {
  rightOverlapList?: FlagInfo[];
  shifted?: boolean;
};
type FlagGroup = graphic.Group & {
  flag_group_inside?: graphic.Group;
  flag_rect?: graphic.Rect;
  flag_text?: graphic.Text;
  flag_line?: graphic.Line;
  flag_circle?: graphic.Circle;
};

/**
 * 用于拆分代码的内部方法
 * @remarks 主要功能是根据旗子的group元素，解构出旗子的各项信息，并添加到旗子信息列表中
 */
function addFlagInfo(out: FlagInfo[], flagItem: FlagGroup) {
  const { flag_rect: rect, flag_line: line, flag_group_inside: insideGroup } = flagItem;
  const boundRect: BoundingRect = {
    x: insideGroup.x,
    y: insideGroup.y,
    height: rect.shape.height,
    width: rect.shape.width
  };
  out.push({
    rect,
    line,
    insideGroup,
    boundRect,
    group: flagItem
  });
}
/**
 * 预处理旗子信息的列表
 */
export function prepareFlagInfoList(chartView: ChartView) {
  const { group } = chartView;
  // 定义旗子各个组成部分的ID的标志性字符信息
  const flagPartNames = ['flag_group_inside', 'flag_rect', 'flag_text', 'flag_line', 'flag_circle'];
  // 用于存储处理完成的旗子信息的列表
  const flagInfoList: FlagInfo[] = [];
  // 遍历标记系列视图View下的每一个旗子
  group.eachChild((flagItem: FlagGroup) => {
    // 对于每一个旗子的group元素，遍历其全部后代元素（包含所有的叶子节点和枝干节点）
    flagItem.traverse(el => {
      // 如果当前元素已经被标记为忽略，则跳过
      if (el.ignore) {
        return;
      }
      // 对于当前元素，依次判别是旗子的哪一个组成部分，如果判定出是具体某部分，则将当前元素挂载到旗子的最外层group上
      flagPartNames.forEach(name => {
        if ((el.extra.dvid as string).indexOf(name) !== -1) {
          flagItem[name] = el;
        }
      });
    });

    // 如果当前旗子没有忽略，则将当前旗子的信息加入到旗子信息列表
    if (!flagItem.ignore) {
      addFlagInfo(flagInfoList, flagItem);
    }
  });

  return flagInfoList;
}

/**
 * 判断两个区间是否相交
 * @param param0
 * @param param1
 * @returns
 */
function isSegmentIntersect([a, b], [c, d]) {
  return d > a && b > c;
}

function useMap<K, V>() {
  const map = new Map<K, V>();
  const get = (key: K) => map.get(key);
  const set = (key: K, value: V) => map.set(key, value);
  return [get, set] as const;
}

// 布局所有标记的位置，不再有重叠的情况
export function shiftLayout(list: FlagInfo[], minBound: number, maxBound: number) {
  const maxInteractions = 100;
  const maxError = 0.001;
  const padding = 2;
  // 定义无需重新布局的标记数量阈值，一般2个以下的标记不需要重新排布
  const SHIFT_THRESHOLD = 2;
  const len = list.length;
  if (len < SHIFT_THRESHOLD) {
    return;
  }

  const [y0, setY0] = useMap<FlagInfo, number>();
  const [y, setY] = useMap<FlagInfo, number>();
  const [h, setH] = useMap<FlagInfo, number>();
  const [xx, setXX] = useMap<FlagInfo, [number, number]>();
  list.forEach(labelInfo => {
    const { x, y: labelY, width, height } = labelInfo.boundRect;
    const [labelLeft, labelRight] = width < 0 ? [x + width, x] : [x, x + width];
    setY0(labelInfo, labelY);
    setY(labelInfo, labelY);
    setH(labelInfo, height);
    // [a, b]
    setXX(labelInfo, [labelLeft, labelRight]);
  });

  for (let iter = 0; iter < maxInteractions; iter += 1) {
    // 根据y方向，从上到下排序
    list.sort((a, b) => y(a) - y(b));
    // 误差
    let error = 0;
    for (let i = 0; i < len - 1; i++) {
      const l0 = list[i];
      let j = i + 1;
      let l1;
      while ((l1 = list[j]) && !isSegmentIntersect(xx(l0), xx(l1))) {
        j += 1;
      }
      if (l1) {
        const l0y0 = y(l0);
        const l0h = h(l0);
        const l1y1 = y(l1);
        const delta = l1y1 - (l0y0 + l0h);
        // eslint-disable-next-line max-depth
        if (delta < padding) {
          const newDelta = (padding - delta) / 2;
          error = Math.max(error, newDelta);
          setY(l0, Math.max(minBound + h(l0) + padding, l0y0 - newDelta));
          setY(l1, Math.min(maxBound - h(l1) - padding, l1y1 + newDelta));
        }
      }
    }

    if (error < maxError) {
      break;
    }
  }

  for (let i = 0; i < len; i += 1) {
    list[i].insideGroup.attr({
      y: y(list[i])
    });
    const { lineYEnd, rectBorderRadius } = shiftStyles(
      y(list[i]),
      list[i].line.shape.y1,
      h(list[i]),
      list[i].rect
    );
    // 旗杆保持和方框正确连接
    list[i].line.attr({
      shape: {
        y2: lineYEnd
      }
    });
    list[i].rect.attr({
      shape: {
        r: rectBorderRadius
      }
    });
  }

  /** 旗子方框的位置调整后，调整旗杆y方向上的终点以及旗子方框的圆角样式
   * @description 旗子方框与旗杆连接处圆角为0
   *  */
  // eslint-disable-next-line no-inner-declarations
  function shiftStyles(flagGroupY: number, lineYStart: number, boundHeight: number, rect) {
    const rectWidth = rect.shape.width;
    let lineYEnd = lineYStart;
    // 圆角数组元素依次对应【左上，右上，右下，左下】
    const rectBorderRadius = Array.isArray(rect.shape.r)
      ? rect.shape.r
      : [rect.shape.r, rect.shape.r, rect.shape.r, rect.shape.r];

    if (flagGroupY > lineYStart) {
      // 如果旗子方框上边界的y坐标 > 旗杆起始点y坐标, 即旗子原点在旗子上方, 则正常连到旗子；
      // 旗子方框与旗杆连接处在上方
      lineYEnd = flagGroupY;
      if (rectWidth > 0) {
        rectBorderRadius[0] = 0;
      } else {
        rectBorderRadius[1] = 0;
      }
    } else if (flagGroupY + Math.abs(boundHeight) > lineYStart) {
      // 否则，如果旗子方框上边界的y坐标 < 旗杆起始点y坐标 < 旗子方框下边界的y坐标, 即旗子原点在旗子内部, 则旗杆长度为0
      // 旗子方框与旗杆连接处旗子方框内部，原本的连接处一侧的圆角需要置为0
      lineYEnd = lineYStart;
      if (rectWidth > 0) {
        rectBorderRadius[0] = 0;
        rectBorderRadius[3] = 0;
      } else {
        rectBorderRadius[1] = 0;
        rectBorderRadius[2] = 0;
      }
    } else {
      // 否则，如果旗子方框下边界的y坐标 < 旗杆起始点y坐标 即旗子原点在旗子下, 则旗杆连接到旗子下边界
      // 旗子方框与旗杆连接处在下方
      lineYEnd = flagGroupY + Math.abs(boundHeight);
      if (rectWidth > 0) {
        rectBorderRadius[3] = 0;
      } else {
        rectBorderRadius[2] = 0;
      }
    }

    return { lineYEnd, rectBorderRadius };
  }
}

export function shiftLayoutOnY(
  list: Pick<FlagLayoutInfo, 'rect' | 'insideGroup' | 'line' | 'boundRect' | 'group'>[],
  topBound: number,
  bottomBound: number
): void {
  return shiftLayout(list, topBound, bottomBound);
}
